import React, { useState } from 'react';
import {
  Box,
  Button,
  AppBar,
  Toolbar,
  Typography,
  useScrollTrigger,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Divider,
  ListItemIcon,
  Tooltip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from '@mui/material';
import {
  AccountCircle as ProfileIcon,
  CreditCard as CreditsIcon,
  DirectionsCar as VehiclesIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Support as SupportIcon,
  Logout as LogoutIcon,
  Search as SearchIcon,
  AddCircle as AddCircleIcon,
  CalendarToday as CalendarIcon,
  Message as MessageIcon,
  Notifications as NotificationsIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Assignment as RequestIcon,
  VerifiedUser as VerifiedIcon,
  Language as LanguageIcon,
  AccountBalance as PayoutsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getInitials, getAvatarColor, getFullImageUrl } from '../../utils/helpers';
import apiService from '../../services/apiService';
import { useEffect } from 'react';
import AlarmModal from './AlarmModal';
import LanguageModal from './LanguageModal';

interface Language {
  code: string;
  name: string;
  flag: string | any;
  isImage?: boolean;
}

interface Props {
  children?: React.ReactElement;
}

function ElevationScroll(props: Props) {
  const { children } = props;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
  });

  return React.cloneElement(children as React.ReactElement<any>, {
    elevation: trigger ? 4 : 0,
    sx: {
      ...(children as React.ReactElement<any>).props.sx,
      bgcolor: trigger ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.7)',
      backdropFilter: 'blur(10px)',
      transition: 'all 0.3s ease',
    },
  });
}

const ModernNavbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [hamburgerMenuAnchor, setHamburgerMenuAnchor] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [alarmModalOpen, setAlarmModalOpen] = useState(false);
  const [alarmCount, setAlarmCount] = useState(0);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const isDriver = user?.isDriver === true || user?.role === 'DRIVER';

  const isRtl = i18n.language.startsWith('fa') || i18n.language.startsWith('ar');

  const getInitialLang = () => {
    const code = i18n.language.startsWith('zh') ? i18n.language : i18n.language.split('-')[0];
    const INTERFACE_LANGS = [
      { code: 'en', name: 'English', flag: '🇺🇸' },
      { code: 'fr', name: 'Français (French)', flag: '🇫🇷' },
      { code: 'es', name: 'Español (Spanish)', flag: '🇪🇸' },
      { code: 'fa', name: 'Farsi', flag: '/assets/images/iran_lion_sun.png', isImage: true },
      { code: 'ar', name: 'العربية (Arabic)', flag: '🇸🇦' },
      { code: 'zh-CN', name: '简体中文 (Simplified Chinese)', flag: '🇨🇳' },
      { code: 'zh-TW', name: '繁體中文 (Traditional Chinese)', flag: '🇹🇼' },
      { code: 'pa', name: 'ਪੰਜਾਬੀ (Punjabi)', flag: '🇮🇳' },
    ];
    return INTERFACE_LANGS.find(l => l.code === code) || INTERFACE_LANGS[0];
  };

  const [targetLang, setTargetLang] = useState<Language>(getInitialLang());

  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);

  const handleLanguageMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageMenuAnchor(event.currentTarget);
  };

  const handleLanguageMenuClose = () => {
    setLanguageMenuAnchor(null);
  };

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    const newLang = [
      { code: 'en', name: 'English', flag: '🇺🇸' },
      { code: 'fr', name: 'Français', flag: '🇫🇷' },
      { code: 'es', name: 'Español', flag: '🇪🇸' },
      { code: 'fa', name: 'فارسی', flag: '/assets/images/iran_lion_sun.png', isImage: true },
      { code: 'ar', name: 'العربية', flag: '🇸🇦' },
      { code: 'zh-CN', name: '简体中文', flag: '🇨🇳' },
      { code: 'zh-TW', name: '繁體中文', flag: '🇹🇼' },
      { code: 'pa', name: 'ਪੰਜਾਬੀ', flag: '🇮🇳' }
    ].find(l => l.code === langCode) || { code: 'en', name: 'English', flag: '🇺🇸' };
    setTargetLang(newLang);
    handleLanguageMenuClose();
  };



  const handleFindRideClick = () => {
    // Always scroll to search widget on homepage
    if (location.pathname === '/') {
      const searchSection = document.getElementById('search-widget-section');
      if (searchSection) {
        searchSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // If on another page, navigate to homepage and then scroll
      navigate('/');
      setTimeout(() => {
        const searchSection = document.getElementById('search-widget-section');
        if (searchSection) {
          searchSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleHamburgerMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setHamburgerMenuAnchor(event.currentTarget);
  };

  const handleHamburgerMenuClose = () => {
    setHamburgerMenuAnchor(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
  };

  const handleScrollToSearch = () => {
    if (location.pathname === '/') {
      const searchSection = document.getElementById('search-widget-section');
      if (searchSection) {
        searchSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate('/');
      setTimeout(() => {
        const searchSection = document.getElementById('search-widget-section');
        if (searchSection) {
          searchSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
    setMobileMenuOpen(false);
  };


  // Load alarm count
  const loadAlarmCount = async () => {
    if (!user) return;
    try {
      const response: any = await apiService.get('/notifications/count');
      if (response.success) {
        setAlarmCount(response.count || 0);
      }
    } catch (error) {
      console.error('Error loading alarm count:', error);
    }
  };

  // Load unread count
  const loadUnreadCount = async () => {
    if (!user) return;
    try {
      const response: any = await apiService.get('/messages/unread-count');
      setUnreadMessageCount(response.unreadCount || 0);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  useEffect(() => {
    if (user) {
      loadAlarmCount();
      loadUnreadCount();
      // Refresh every 60 seconds
      const interval = setInterval(() => {
        loadAlarmCount();
        loadUnreadCount();
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const featureItems = [
    { icon: <SearchIcon sx={{ fontSize: '1.25rem' }} />, label: t('navbar.find'), path: '/#search-widget-section', onClick: handleScrollToSearch },
    {
      icon: <AddCircleIcon sx={{ fontSize: '1.25rem' }} />, label: t('navbar.offer'), path: '/create-ride', onClick: () => {
        if (user) {
          navigate('/create-ride');
        } else {
          navigate(`/login?redirect=${encodeURIComponent('/create-ride')}`);
        }
        setMobileMenuOpen(false);
      }
    },
    {
      icon: <RequestIcon sx={{ fontSize: '1.25rem' }} />, label: t('navbar.request'), path: '/request-ride', onClick: () => {
        if (user) {
          navigate('/request-ride');
        } else {
          navigate(`/login?redirect=${encodeURIComponent('/request-ride')}`);
        }
        setMobileMenuOpen(false);
      }
    },
    { icon: <VehiclesIcon sx={{ fontSize: '1.25rem' }} />, label: t('navbar.myRides'), path: '/my-rides', onClick: () => { navigate('/my-rides'); setMobileMenuOpen(false); } },
    {
      icon: <SearchIcon sx={{ fontSize: '1.25rem' }} />, label: t('navbar.bids'), path: '/browse-requests', onClick: () => {
        if (user) {
          navigate('/browse-requests');
        } else {
          navigate(`/login?redirect=${encodeURIComponent('/browse-requests')}`);
        }
        setMobileMenuOpen(false);
      }
    },
    {
      icon: (
        <Badge badgeContent={unreadMessageCount} color="error">
          <MessageIcon sx={{ fontSize: '1.25rem' }} />
        </Badge>
      ),
      label: t('navbar.inbox'),
      path: '/inbox',
      onClick: () => { navigate('/inbox'); setMobileMenuOpen(false); }
    },
  ];

  return (
    <>
      <ElevationScroll>
        <AppBar
          position="fixed"
          sx={{
            top: 16,
            left: 0,
            right: 0,
            margin: '0 auto',
            width: { xs: 'calc(100% - 32px)', md: 'calc(100% - 64px)', lg: '720px' },
            bgcolor: 'rgba(255, 255, 255, 0.82)',
            backdropFilter: 'blur(16px)',
            borderRadius: '100px',
            boxShadow: '0 2px 16px rgba(0,0,0,0.07), 0 0 0 1px rgba(0,0,0,0.04)',
            border: 'none',
          }}
        >
          <Toolbar
            sx={{
              justifyContent: 'space-between',
              px: { xs: 1.5, md: 2 },
              minHeight: { xs: 60, md: 64 },
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                cursor: 'pointer',
                flexShrink: 0,
                mr: { xs: 0.2, md: 0.4 },
              }}
              onClick={() => navigate('/')}
            >
              <Box
                component="img"
                src="/Pic/Logo.svg"
                alt="Ride Club"
                sx={{
                  width: { xs: 32, md: 36 },
                  height: { xs: 32, md: 36 },
                  borderRadius: '10px',
                  objectFit: 'contain',
                }}
              />
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '0.95rem', md: '1rem' },
                  letterSpacing: '-0.01em',
                  color: '#0f172a',
                  display: { xs: 'none', sm: 'block' },
                }}
              >
                Ride Club
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flex: 1,
                justifyContent: 'center',
                minWidth: 0,
              }}
            >
            {/* Hamburger Menu (Mobile/Tablet — authenticated users only) */}
            {user && (
              <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
                <IconButton
                  onClick={handleHamburgerMenuOpen}
                  sx={{
                    color: '#1a1a1a',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.05)',
                    },
                    borderRadius: '16px',
                  }}
                >
                  <MenuIcon sx={{ fontSize: '1.625rem' }} />
                </IconButton>
              </Box>
            )}

            {/* Desktop Feature Dock (Visible when Logged In on Desktop) */}
            {user ? (
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' }, // Hide on mobile/tablet, show on desktop
                  alignItems: 'center',
                  bgcolor: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: '100px',
                  p: 0.4,
                  gap: 0.25,
                }}
              >
                {featureItems.map((item, idx) => (
                  <Button
                    key={idx}
                    onClick={item.onClick}
                    startIcon={item.icon}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: '100px',
                      color: '#374151',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      minWidth: 0,
                      '&:hover': {
                        color: '#10b981',
                        bgcolor: 'white',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    <Box sx={{ display: { xs: 'none', lg: 'block' } }}>{item.label}</Box>
                  </Button>
                ))}
              </Box>
            ) : (
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <Button
                  sx={{
                    color: '#374151',
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: '100px',
                    '&:hover': {
                      color: '#10b981',
                      bgcolor: 'rgba(16,185,129,0.06)',
                    },
                  }}
                  onClick={handleScrollToSearch}
                >
                  {t('navbar.findARide')}
                </Button>
                <Button
                  sx={{
                    color: '#374151',
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: '100px',
                    '&:hover': {
                      color: '#10b981',
                      bgcolor: 'rgba(16,185,129,0.06)',
                    },
                  }}
                  onClick={() => {
                    if (user) {
                      navigate('/create-ride');
                    } else {
                      navigate(`/login?redirect=${encodeURIComponent('/create-ride')}`);
                    }
                  }}
                >
                  {t('navbar.offerARide')}
                </Button>
                <Button
                  sx={{
                    color: '#374151',
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    px: 1.25,
                    py: 0.75,
                    borderRadius: '100px',
                    '&:hover': {
                      color: '#10b981',
                      bgcolor: 'rgba(16,185,129,0.06)',
                    },
                  }}
                  onClick={() => {
                    if (user) {
                      navigate('/request-ride');
                    } else {
                      navigate(`/login?redirect=${encodeURIComponent('/request-ride')}`);
                    }
                  }}
                >
                  {t('navbar.requestARide')}
                </Button>
              </Box>
            )}

            </Box>
            {/* Auth Buttons / User Profile */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              {/* Global Language Selector Dropdown */}
              <Tooltip title={t('navbar.language', 'Language')}>
                <IconButton
                  onClick={handleLanguageMenuOpen}
                  sx={{
                    color: '#4b5563',
                    bgcolor: 'rgba(0,0,0,0.04)',
                    border: '1px solid rgba(0,0,0,0.08)',
                    '&:hover': {
                      bgcolor: 'rgba(16, 185, 129, 0.1)',
                      color: '#10b981',
                      borderColor: '#10b981',
                    },
                    borderRadius: '100px',
                    px: 1,
                    py: 0.6,
                    gap: 0.5,
                  }}
                >
                  <LanguageIcon sx={{ fontSize: '1rem' }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', display: { xs: 'none', sm: 'block' } }}>
                    {i18n.language.startsWith('en') ? 'EN' : 
                     i18n.language.startsWith('fr') ? 'FR' : 
                     i18n.language.startsWith('es') ? 'ES' : 
                     i18n.language.startsWith('fa') ? 'FA' : 
                     i18n.language.startsWith('zh-CN') ? '简' :
                     i18n.language.startsWith('zh-TW') ? '繁' :
                     i18n.language.startsWith('pa') ? 'PA' :
                     i18n.language.startsWith('hi') ? 'HI' :
                     i18n.language.startsWith('ar') ? 'AR' : 'AR'}
                  </Typography>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={languageMenuAnchor}
                open={Boolean(languageMenuAnchor)}
                onClose={handleLanguageMenuClose}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    minWidth: 150,
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                    '& .MuiMenuItem-root': {
                      fontSize: '1.2rem',
                      fontWeight: 600,
                      gap: 2,
                      py: 1.5,
                    },
                  },
                }}
              >
                <MenuItem 
                  onClick={() => changeLanguage('en')}
                  selected={i18n.language.startsWith('en')}
                >
                  <Box 
                    component="span" 
                    sx={{ 
                      fontSize: '0.8rem', 
                      fontWeight: 700, 
                      color: 'text.secondary', 
                      bgcolor: 'rgba(0,0,0,0.06)', 
                      borderRadius: '6px', 
                      px: 1.2, 
                      py: 0.4,
                      minWidth: '36px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    EN
                  </Box> English
                </MenuItem>
                <MenuItem 
                  onClick={() => changeLanguage('fr')}
                  selected={i18n.language.startsWith('fr')}
                >
                  <Box 
                    component="span" 
                    sx={{ 
                      fontSize: '0.8rem', 
                      fontWeight: 700, 
                      color: 'text.secondary', 
                      bgcolor: 'rgba(0,0,0,0.06)', 
                      borderRadius: '6px', 
                      px: 1.2, 
                      py: 0.4,
                      minWidth: '36px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    FR
                  </Box> Français
                </MenuItem>
                <MenuItem 
                  onClick={() => changeLanguage('es')}
                  selected={i18n.language.startsWith('es')}
                >
                  <Box 
                    component="span" 
                    sx={{ 
                      fontSize: '0.8rem', 
                      fontWeight: 700, 
                      color: 'text.secondary', 
                      bgcolor: 'rgba(0,0,0,0.06)', 
                      borderRadius: '6px', 
                      px: 1.2, 
                      py: 0.4,
                      minWidth: '36px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    ES
                  </Box> Español
                </MenuItem>
                <MenuItem 
                  onClick={() => changeLanguage('fa')}
                  selected={i18n.language.startsWith('fa')}
                >
                  <Box 
                    component="span" 
                    sx={{ 
                      fontSize: '0.8rem', 
                      fontWeight: 700, 
                      color: 'text.secondary', 
                      bgcolor: 'rgba(0,0,0,0.06)', 
                      borderRadius: '6px', 
                      px: 1.2, 
                      py: 0.4,
                      minWidth: '36px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    FA
                  </Box> فارسی
                </MenuItem>
                <MenuItem 
                  onClick={() => changeLanguage('ar')}
                  selected={i18n.language.startsWith('ar')}
                >
                  <Box 
                    component="span" 
                    sx={{ 
                      fontSize: '0.8rem', 
                      fontWeight: 700, 
                      color: 'text.secondary', 
                      bgcolor: 'rgba(0,0,0,0.06)', 
                      borderRadius: '6px', 
                      px: 1.2, 
                      py: 0.4,
                      minWidth: '36px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    AR
                  </Box> العربية
                </MenuItem>
                <MenuItem 
                  onClick={() => changeLanguage('zh-CN')}
                  selected={i18n.language.startsWith('zh-CN')}
                >
                  <Box 
                    component="span" 
                    sx={{ 
                      fontSize: '0.8rem', 
                      fontWeight: 700, 
                      color: 'text.secondary', 
                      bgcolor: 'rgba(0,0,0,0.06)', 
                      borderRadius: '6px', 
                      px: 1.2, 
                      py: 0.4,
                      minWidth: '36px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    简
                  </Box> 简体中文
                </MenuItem>
                <MenuItem 
                  onClick={() => changeLanguage('zh-TW')}
                  selected={i18n.language.startsWith('zh-TW')}
                >
                  <Box 
                    component="span" 
                    sx={{ 
                      fontSize: '0.8rem', 
                      fontWeight: 700, 
                      color: 'text.secondary', 
                      bgcolor: 'rgba(0,0,0,0.06)', 
                      borderRadius: '6px', 
                      px: 1.2, 
                      py: 0.4,
                      minWidth: '36px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    繁
                  </Box> 繁體中文
                </MenuItem>
                <MenuItem 
                  onClick={() => changeLanguage('pa')}
                  selected={i18n.language.startsWith('pa')}
                >
                  <Box 
                    component="span" 
                    sx={{ 
                      fontSize: '0.8rem', 
                      fontWeight: 700, 
                      color: 'text.secondary', 
                      bgcolor: 'rgba(0,0,0,0.06)', 
                      borderRadius: '6px', 
                      px: 1.2, 
                      py: 0.4,
                      minWidth: '36px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    PA
                  </Box> ਪੰਜਾਬੀ
                </MenuItem>
                <MenuItem 
                  onClick={() => changeLanguage('hi')}
                  selected={i18n.language.startsWith('hi')}
                >
                  <Box 
                    component="span" 
                    sx={{ 
                      fontSize: '0.8rem', 
                      fontWeight: 700, 
                      color: 'text.secondary', 
                      bgcolor: 'rgba(0,0,0,0.06)', 
                      borderRadius: '6px', 
                      px: 1.2, 
                      py: 0.4,
                      minWidth: '36px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    HI
                  </Box> हिन्दी
                </MenuItem>
              </Menu>

              {user ? (
                /* Authenticated user - show profile menu */
                <>
                  <Tooltip title={t('navbar.rideAlarms')}>
                    <IconButton
                      onClick={() => setAlarmModalOpen(true)}
                      sx={{
                        color: '#9ca3af',
                        '&:hover': {
                          color: '#10b981',
                        },
                        position: 'relative',
                      }}
                    >
                      <Badge badgeContent={alarmCount > 0 ? alarmCount : 0} color="error">
                        <NotificationsIcon sx={{ fontSize: '2.25rem' }} />
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('navbar.profile')}>
                    <IconButton
                      onClick={handleProfileMenuOpen}
                      sx={{
                        p: 0,
                        '&:hover': {
                          transform: 'scale(1.05)',
                        },
                        transition: 'transform 0.2s',
                      }}
                    >
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          user?.isLicenseVerified ? (
                            <VerifiedIcon
                              sx={{
                                fontSize: 16,
                                color: '#4caf50',
                                bgcolor: 'white',
                                borderRadius: '50%',
                                p: 0.25,
                              }}
                            />
                          ) : null
                        }
                      >
                        <Avatar
                          sx={{
                            bgcolor: getAvatarColor((user?.firstName || '') + (user?.lastName || '')),
                            width: 44,
                            height: 44,
                            border: '2px solid white',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                          }}
                          src={getFullImageUrl(user?.profilePicture) || undefined}
                        >
                          {getInitials(user?.firstName || '', user?.lastName || '')}
                        </Avatar>
                      </Badge>
                    </IconButton>
                  </Tooltip>
                  <Menu
                    anchorEl={profileMenuAnchor}
                    open={Boolean(profileMenuAnchor)}
                    onClose={handleProfileMenuClose}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    PaperProps={{
                      sx: {
                        mt: 1.5,
                        minWidth: 200,
                        borderRadius: '12px',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                        '& .MuiMenuItem-root': {
                          fontSize: '1.3125rem', // 50% increase from default 0.875rem
                        },
                      },
                    }}
                  >
                    <MenuItem
                      onClick={() => {
                        navigate('/profile');
                        handleProfileMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <ProfileIcon sx={{ fontSize: '1.5rem' }} />
                      </ListItemIcon>
                      {t('navbar.profile')}
                    </MenuItem>
                    {isDriver && (
                      <>
                        <MenuItem
                          onClick={() => {
                            navigate('/credits');
                            handleProfileMenuClose();
                          }}
                        >
                          <ListItemIcon>
                            <CreditsIcon sx={{ fontSize: '1.5rem' }} />
                          </ListItemIcon>
                          {t('navbar.credits')}
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            navigate('/profile?tab=payouts');
                            handleProfileMenuClose();
                          }}
                        >
                          <ListItemIcon>
                            <PayoutsIcon sx={{ fontSize: '1.5rem' }} />
                          </ListItemIcon>
                          {t('navbar.payouts', 'Payouts')}
                        </MenuItem>
                      </>
                    )}
                    <MenuItem
                      onClick={() => {
                        navigate('/vehicles');
                        handleProfileMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <VehiclesIcon sx={{ fontSize: '1.5rem' }} />
                      </ListItemIcon>
                      {t('navbar.vehicles')}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        navigate('/settings');
                        handleProfileMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <SettingsIcon sx={{ fontSize: '1.5rem' }} />
                      </ListItemIcon>
                      {t('navbar.settings')}
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        navigate('/help');
                        handleProfileMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <HelpIcon sx={{ fontSize: '1.5rem' }} />
                      </ListItemIcon>
                      {t('navbar.help')}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        navigate('/support');
                        handleProfileMenuClose();
                      }}
                    >
                      <ListItemIcon>
                        <SupportIcon sx={{ fontSize: '1.5rem' }} />
                      </ListItemIcon>
                      {t('navbar.support')}
                    </MenuItem>
                    <Divider />
                    <MenuItem onClick={handleLogout}>
                      <ListItemIcon>
                        <LogoutIcon sx={{ fontSize: '1.5rem' }} />
                      </ListItemIcon>
                      {t('navbar.logout')}
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                /* Non-authenticated user - show login/signup buttons */
                <>
                  <Button
                    sx={{
                      color: '#0f172a',
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: '100px',
                      display: { xs: 'none', sm: 'inline-flex' },
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.05)',
                        color: '#10b981',
                      },
                    }}
                    onClick={() => navigate('/login')}
                  >
                    {t('navbar.login')}
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      bgcolor: '#10b981',
                      color: 'white',
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 700,
                      px: 2,
                      py: 0.875,
                      borderRadius: '100px',
                      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
                      '&:hover': {
                        bgcolor: '#059669',
                        boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                      },
                      transition: 'all 0.15s',
                      '&:active': {
                        transform: 'scale(0.97)',
                      },
                    }}
                    onClick={() => navigate('/register')}
                  >
                    {t('navbar.signUp')}
                  </Button>
                </>
              )}
              {/* Mobile Menu Button (for non-authenticated users) */}
              {!user && (
                <IconButton
                  sx={{
                    display: { md: 'none' },
                    color: '#1a1a1a',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.05)',
                    },
                    borderRadius: '16px',
                  }}
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                >
                  {mobileMenuOpen ? <CloseIcon sx={{ fontSize: '1.625rem' }} /> : <MenuIcon sx={{ fontSize: '1.625rem' }} />}
                </IconButton>
              )}
            </Box>
          </Toolbar>
        </AppBar>
      </ElevationScroll>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: { xs: 78, md: 0 },
            left: 16,
            right: 16,
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '40px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
            p: 3,
            zIndex: 1300,
            border: '1px solid rgba(0, 0, 0, 0.05)',
            display: { md: 'none' },
            animation: 'fadeIn 0.3s ease-in',
            '@keyframes fadeIn': {
              from: {
                opacity: 0,
                transform: 'translateY(-20px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          {user && (
            <>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1.5,
                  mb: 3,
                }}
              >
                {featureItems.map((item, idx) => (
                  <Button
                    key={idx}
                    onClick={item.onClick}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 1,
                      p: 2,
                      bgcolor: 'rgba(0, 0, 0, 0.02)',
                      borderRadius: '24px',
                      color: '#6b7280',
                      '&:hover': {
                        bgcolor: 'rgba(16, 185, 129, 0.1)',
                        color: '#10b981',
                        '& svg': {
                          transform: 'scale(1.1)',
                        },
                      },
                      transition: 'all 0.2s',
                    }}
                  >
                    <Box sx={{ color: 'inherit' }}>{item.icon}</Box>
                    <Typography
                      sx={{
                        fontSize: '0.6875rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Button>
                ))}
                <Button
                  onClick={() => {
                    navigate('/settings');
                    setMobileMenuOpen(false);
                  }}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    p: 2,
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                    borderRadius: '24px',
                    color: '#6b7280',
                    '&:hover': {
                      bgcolor: 'rgba(16, 185, 129, 0.1)',
                      color: '#10b981',
                    },
                  }}
                >
                  <SettingsIcon sx={{ fontSize: '1.25rem' }} />
                  <Typography
                    sx={{
                      fontSize: '0.6875rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    {t('navbar.settings')}
                  </Typography>
                </Button>
              </Box>
              <Divider sx={{ my: 2 }} />
            </>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button
              onClick={handleScrollToSearch}
              sx={{
                textAlign: 'left',
                fontSize: '1.875rem',
                fontWeight: 700,
                color: '#000000',
                p: 1,
                textTransform: 'none',
                '&:hover': {
                  color: '#10b981',
                  bgcolor: 'transparent',
                },
              }}
            >
              {t('navbar.findARide')}
            </Button>
            <Button
              onClick={() => {
                if (user) {
                  navigate('/create-ride');
                } else {
                  navigate(`/login?redirect=${encodeURIComponent('/create-ride')}`);
                }
                setMobileMenuOpen(false);
              }}
              sx={{
                textAlign: 'left',
                fontSize: '1.875rem',
                fontWeight: 700,
                color: '#000000',
                p: 1,
                textTransform: 'none',
                '&:hover': {
                  color: '#10b981',
                  bgcolor: 'transparent',
                },
              }}
            >
              {t('navbar.offerARide')}
            </Button>
            <Button
              onClick={() => {
                navigate('/help');
                setMobileMenuOpen(false);
              }}
              sx={{
                textAlign: 'left',
                fontSize: '1.875rem',
                fontWeight: 700,
                color: '#000000',
                p: 1,
                textTransform: 'none',
                '&:hover': {
                  color: '#10b981',
                  bgcolor: 'transparent',
                },
              }}
            >
              {t('navbar.safetyCenter')}
            </Button>
            <Button
              onClick={() => {
                navigate('/help');
                setMobileMenuOpen(false);
              }}
              sx={{
                textAlign: 'left',
                fontSize: '1.875rem',
                fontWeight: 700,
                color: '#000000',
                p: 1,
                textTransform: 'none',
                '&:hover': {
                  color: '#10b981',
                  bgcolor: 'transparent',
                },
              }}
            >
              {t('navbar.helpAndSupport')}
            </Button>
          </Box>

          {!user && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  navigate('/register');
                  setMobileMenuOpen(false);
                }}
                sx={{
                  py: 2,
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  bgcolor: '#10b981',
                  borderRadius: '24px',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: '#059669',
                  },
                }}
              >
                {t('navbar.signUpNow')}
              </Button>
              <Button
                fullWidth
                onClick={() => {
                  navigate('/login');
                  setMobileMenuOpen(false);
                }}
                sx={{
                  py: 2,
                  fontSize: '1rem',
                  fontWeight: 700,
                  color: '#6b7280',
                  bgcolor: 'rgba(0, 0, 0, 0.05)',
                  borderRadius: '24px',
                  textTransform: 'none',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                {t('navbar.login')}
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Hamburger Menu Dropdown (Mobile/Tablet only) */}
      {user && (
        <Menu
          anchorEl={hamburgerMenuAnchor}
          open={Boolean(hamburgerMenuAnchor)}
          onClose={handleHamburgerMenuClose}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 200,
              borderRadius: '12px',
              boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
              '& .MuiMenuItem-root': {
                fontSize: '1.3125rem',
              },
            },
          }}
        >
          {featureItems.map((item, idx) => (
            <MenuItem
              key={idx}
              onClick={() => {
                item.onClick();
                handleHamburgerMenuClose();
              }}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              {item.label}
            </MenuItem>
          ))}
        </Menu>
      )}


      {/* Alarm Modal */}
      <AlarmModal
        open={alarmModalOpen}
        onClose={() => {
          setAlarmModalOpen(false);
          loadAlarmCount(); // Refresh count when modal closes
        }}
      />
    </>
  );
};

export default ModernNavbar;

